import { useEffect, useState } from "react";
import { isNativePlatform } from "@/lib/platform";

const MOBILE_MAX_WIDTH = 767;
const MOBILE_DEVICE_REGEX = /Android|iPhone|iPad|iPod|IEMobile|Windows Phone/i;

type NavigatorWithStandalone = Navigator & { standalone?: boolean; userAgentData?: { mobile?: boolean } };

const isLikelyMobileDevice = (): boolean => {
  // A Capacitor native app is, by definition, a mobile device.
  if (isNativePlatform()) return true;
  if (typeof navigator === "undefined") return false;

  const nav = navigator as NavigatorWithStandalone;
  if (typeof nav.userAgentData?.mobile === "boolean") {
    return nav.userAgentData.mobile;
  }

  return MOBILE_DEVICE_REGEX.test(navigator.userAgent);
};

const isStandaloneDisplayMode = (): boolean => {
  // The native app behaves like an installed, standalone app.
  if (isNativePlatform()) return true;
  if (typeof window === "undefined") return false;

  const nav = navigator as NavigatorWithStandalone;
  const hasStandaloneMedia = window.matchMedia("(display-mode: standalone)").matches;
  const hasIosStandalone = nav.standalone === true;
  const hasTwaReferrer = document.referrer.startsWith("android-app://");

  return hasStandaloneMedia || hasIosStandalone || hasTwaReferrer;
};

const isNarrowViewport = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
};

export const isInstalledMobileAppContext = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  return isStandaloneDisplayMode() && (isLikelyMobileDevice() || isNarrowViewport());
};

export const useInstalledMobileAppContext = (): boolean => {
  const [isInstalledMobileApp, setIsInstalledMobileApp] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const update = () => setIsInstalledMobileApp(isInstalledMobileAppContext());

    update();
    mediaQuery.addEventListener("change", update);
    window.addEventListener("resize", update);

    return () => {
      mediaQuery.removeEventListener("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return isInstalledMobileApp;
};

export const isMobileRouteContext = (): boolean => {
  if (typeof window === "undefined") return false;
  return isNarrowViewport() || isInstalledMobileAppContext();
};

export const useMobileRouteContext = (): boolean => {
  const [isMobileRoute, setIsMobileRoute] = useState(false);

  useEffect(() => {
    const displayModeQuery = window.matchMedia("(display-mode: standalone)");
    const viewportQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const update = () => setIsMobileRoute(isMobileRouteContext());

    update();
    displayModeQuery.addEventListener("change", update);
    viewportQuery.addEventListener("change", update);
    window.addEventListener("resize", update);

    return () => {
      displayModeQuery.removeEventListener("change", update);
      viewportQuery.removeEventListener("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return isMobileRoute;
};
